/**
 * @file request-outcome.tsx
 * @module app/modules/service-request/components/request-outcome
 * @description Shows who decided a service request and what happened, under its status. Used by
 * the requester's table and the approver's cards.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { ExternalLink } from 'lucide-react';
import Method from '../../../../utils/methods';
import {
  ServiceRequest,
  ServiceRequestStatus,
} from '../types/service-request.types';

const Status = ServiceRequestStatus;

export const formatRequestDate = (value?: string) =>
  value ? Method.convertDateToDDMMYYYYHHMMAMPM(value) : '';

/** "Decided by Name · date", leaving out whichever part is missing. */
const byLine = (name?: string, at?: string) =>
  [name ? `Decided by ${name}` : '', formatRequestDate(at)]
    .filter(Boolean)
    .join(' · ');

/** Labelled reason box: red for declined and failed, grey for withdrawn. */
const Note = ({
  label,
  text,
  neutral,
}: {
  label: string;
  text?: string;
  neutral?: boolean;
}) =>
  text ? (
    <div className={`sr-reason${neutral ? ' sr-reason--neutral' : ''}`}>
      <span className="sr-reason__label">{label}</span>
      {text}
    </div>
  ) : null;

/** The Jira ticket: a link to it, whether it is unassigned, who sent it, and why. */
const JiraTicket = ({ request }: { request: ServiceRequest }) => {
  const ticket = request.jiraTicket;
  if (!ticket?.key) return null;
  return (
    <div className="sr-jira">
      <div className="sr-jira__line">
        <a
          className="sr-jira__link"
          href={ticket.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ticket.key}
          <ExternalLink size={12} aria-hidden="true" />
        </a>
        {ticket.assigned ? null : <span className="sr-muted">Unassigned</span>}
        <span className="sr-muted">{formatRequestDate(ticket.sentAt)}</span>
      </div>
      <Note
        label={`Sent to IT by ${ticket.sentBy?.name || 'an approver'}`}
        text={ticket.reason}
        neutral
      />
    </div>
  );
};

const RequestOutcome = ({ request }: { request: ServiceRequest }) => {
  const { status, decidedBy, decidedAt, result } = request;
  if (status === Status.Pending) return null;
  if (status === Status.SentToJira) {
    return (
      <div className="sr-outcome">
        <JiraTicket request={request} />
      </div>
    );
  }
  if (status === Status.Withdrawn) {
    return (
      <div className="sr-outcome">
        <div className="sr-muted">{formatRequestDate(request.withdrawnAt)}</div>
        <Note label="Reason" text={request.withdrawReason} neutral />
      </div>
    );
  }
  return (
    <div className="sr-outcome">
      <div className="sr-muted">{byLine(decidedBy?.name, decidedAt)}</div>
      {status === Status.Processing ? (
        <div className="sr-muted">Running the action...</div>
      ) : null}
      {status === Status.Completed && result?.message ? (
        <div className="sr-result">{result.message}</div>
      ) : null}
      {status === Status.Failed ? (
        <Note label="Failed" text={result?.message} />
      ) : null}
      {status === Status.Declined ? (
        <Note label="Decline reason" text={request.decisionComment} />
      ) : null}
      <JiraTicket request={request} />
    </div>
  );
};

export default RequestOutcome;
